<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-body p-4">
        <!-- Single Line Filter Row -->
        <div class="filter-row-single">

          <!-- Return Type -->
          <div class="filter-item">
            <label class="filter-label">Return Type:</label>
            <select v-model="returnType" class="form-select filter-control">
              <option value="with_dispatch">Dispatch</option>
              <option value="without_dispatch">Non Dispatch</option>
            </select>
          </div>

          <!-- Vehicle Dropdown (Only for With Dispatch) -->
          <div class="filter-item" v-if="returnType === 'with_dispatch'">
            <label class="filter-label">Vehicle No:</label>
            <select v-model="selectedVehicleNo" class="form-select filter-control">
              <option disabled value="">Select Vehicle No</option>
              <option v-for="(item, index) in vehicleNumbers" :key="index" :value="item.id">
                {{ item.vehicle_no }}
              </option>
            </select>
          </div>

          <!-- Sales Rep Dropdown (Only for Without Dispatch) -->
          <div class="filter-item" v-if="returnType === 'without_dispatch'">
            <label class="filter-label" for="reps">Sales Rep:</label>
            <select v-model="selectedSalesRep" class="form-select filter-control">
              <option disabled value="">Select Sales Rep</option>
              <option v-for="rep in salesRep" :key="rep.profile_id" :value="String(rep.profile_id)">
                {{ rep.name }}
              </option>
            </select>
          </div>

          <!-- Date Picker -->
          <div class="filter-item">
            <label class="filter-label">{{ returnType === 'with_dispatch' ? 'Dispatch Date:' : 'Return Date:' }}</label>
            <input
              type="date"
              v-model="startDate"
              class="form-control filter-control"
              :max="currentDate"
            />
          </div>

          <!-- Search Button -->
          <div class="filter-item button-item">
            <button @click="performSearch" class="btn button-default" :disabled="loading">
              <span>Search</span>
            </button>
          </div>

        </div>

        <!--Loading Indicator-->
        <div v-if="loading" class="loading-spinner py-4">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <!--Show Item List in the Table-->
        <div v-if="showTable && dispatchItems.length > 0" class="modern-table-card mt-4">
          <div class="table-responsive table-scroll-container">
            <table class="table modern-table mb-0">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-left header-cell group-end col-item-name">Item Name</th>
                  <th v-if="returnType === 'with_dispatch'" rowspan="2" class="align-middle text-center header-cell group-end">Loaded</th>
                  <th v-if="returnType === 'with_dispatch'" rowspan="2" class="align-middle text-center header-cell group-end">Invoiced</th>
                  <th v-if="returnType === 'with_dispatch'" rowspan="2" class="align-middle text-center header-cell group-end">Balance</th>
                  <th colspan="2" class="text-center header-cell group-header group-end">Return</th>
                  <th colspan="2" class="text-center header-cell group-header">Counted Quantity</th>
                </tr>
                <tr class="text-center">
                  <th class="header-cell sub-header">Normal</th>
                  <th class="header-cell sub-header group-end">Damaged</th>
                  <th class="header-cell sub-header col-counted">Normal</th>
                  <th class="header-cell sub-header col-counted">Damaged</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in dispatchItems"
                  :key="index"
                  class="modern-row"
                  :class="{ 'row-error': item._hasError }"
                >
                  <td class="item-name-cell">
                    <div class="d-flex align-items-center">
                      <span>{{ item.item_name }}</span>
                    </div>
                  </td>
                  <td v-if="returnType === 'with_dispatch'" class="text-center font-weight-medium">
                    <span class="qty-badge">{{ parseInt(item.qty) }}</span>
                  </td>
                  <td v-if="returnType === 'with_dispatch'" class="text-center font-weight-medium">
                    <span class="qty-badge invoiced">{{ parseInt(item.invoiced_qty) }}</span>
                  </td>
                  <td v-if="returnType === 'with_dispatch'" class="text-center">
                    <span class="balance-badge" :class="{ 'has-balance': computedBalance(item) > 0 }">
                      {{ computedBalance(item) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="return-badge normal" :class="{ 'is-zero': parseInt(item.normal_return_qty) === 0 }">
                      {{ parseInt(item.normal_return_qty) }}
                    </span>
                  </td>
                  <td class="text-center group-end">
                    <span class="return-badge damage" :class="{ 'has-damage': parseInt(item.damage_return_qty) > 0 }">
                      {{ parseInt(item.damage_return_qty) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <input
                      type="number"
                      v-model="item.normal_counted_qty"
                      class="form-control quantity-input modern-input mx-auto"
                      :class="{ 'is-invalid': item._hasError }"
                      min="0"
                      placeholder="0"
                      :disabled="gatePassSaved"
                      @input="clearRowError(item)"
                    />
                  </td>
                  <td class="text-center">
                    <input
                      type="number"
                      v-model="item.damage_counted_qty"
                      class="form-control quantity-input modern-input mx-auto"
                      :class="{ 'is-invalid': item._hasError }"
                      min="0"
                      placeholder="0"
                      :disabled="gatePassSaved"
                      @input="clearRowError(item)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modern Empty State Message -->
        <div v-else-if="showTable && dispatchItems.length === 0" class="modern-empty-state mt-4">
          <div class="empty-state-icon-wrapper">
            <i class="fa fa-inbox empty-state-icon"></i>
          </div>
          <h4 class="empty-state-title">No Gate Pass In Records Found</h4>
          <p class="empty-state-description">
            There are no gate pass in records found for the selected vehicle and date.
          </p>
        </div>

        <!--Show Error Message-->
        <div v-if="errorMessage" class="error-message mt-3">{{ errorMessage }}</div>

        <!-- Remark Textarea -->
        <div v-if="dispatchItems.length > 0" class="mt-4 modern-remark-container">
          <label class="mb-2 font-weight-bold">Remark:</label>
          <textarea
            v-model="gatePassRemark"
            class="form-control w-100 modern-textarea"
            rows="2"
            placeholder="Enter remark..."
            :disabled="gatePassSaved"
          ></textarea>
        </div>

        <!--Gate Pass In Save Button-->
        <div v-if="dispatchItems.length > 0" class="d-flex justify-content-end mt-4">
          <button
              v-if="!gatePassSaved"
              type="button"
              class="btn btn-success px-4 py-2 font-weight-semibold shadow-sm"
              @click="issueGatePass"
              :disabled="loading2"
          >
            <span v-if="loading2" class="spinner-border spinner-border-sm mr-2"></span>
            <span>{{ loading2 ? 'Saving...' : 'Save Gate Pass In' }}</span>
          </button>

          <button
              v-else
              type="button"
              class="btn btn-primary px-4 py-2 font-weight-semibold shadow-sm"
              @click="printGatePass"
          >
            <span>Print Gate Pass</span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { fetchVehicleListByProfileGateIn, fetchDispatchItemsSearchGatePassIn, dispatchlistitem, savegetpassin,  fetchSalesReps, } from '@/services/dataService';
import { useToast } from "vue-toastification";

export default {
  data() {
    const currentDate = new Date().toISOString().split('T')[0];
    return {
      selectedVehicleNo: '',
      vehicleNumbers: [],
      dispatchItems: [],
      startDate: currentDate,
      currentDate: new Date().toISOString().split('T')[0],
      token: localStorage.getItem('token_set'),
      showTable: false, // Flag to control table visibility
      profileId: localStorage.getItem('profile_id'), // Added profileId
      reportingProfileId: localStorage.getItem('reportingL1ProfileId') || '',
      userCategory: localStorage.getItem('user_label') || '',
      loading: false, // Loading state
      loading2: false, // Loading when gate pass is save
      vehicleError: '', // Error message for vehicle selection
      gatePassSaved: false,
      distributorName: '',
      returnType: 'with_dispatch',
      salesRep: [],
      selectedSalesRep: '',
      validationError: '',
      gatePassRemark: '',
    };
  },

  created() {
    this.loadVehicleNumbers();
    this.fetchSalesRepresentatives();
  },

  async mounted() {
    const distributor = localStorage.getItem('full_name');

    if (distributor) {
      this.distributorName = distributor;
    }

    await this.handleQueryParams();
  },

  watch: {
    returnType() {
      this.resetForm();
    },
    '$route.query': {
      handler() {
        this.handleQueryParams();
      },
      deep: true
    }
  },

  methods: {
    async handleQueryParams() {
      const { return_type, vehicle_id, date } = this.$route.query;

      if (return_type || vehicle_id || date) {
        if (return_type) {
          const rt = String(return_type).toLowerCase();
          if (rt === 'dispatch' || rt === 'with_dispatch') {
            this.returnType = 'with_dispatch';
          } else if (rt === 'non dispatch' || rt === 'without_dispatch') {
            this.returnType = 'without_dispatch';
          }
        }
        if (date) {
          this.startDate = date;
        }
        if (this.returnType === 'with_dispatch') {
          await this.loadVehicleNumbers();
        } else if (this.returnType === 'without_dispatch') {
          await this.fetchSalesRepresentatives();
        }

        if (vehicle_id && this.returnType === 'with_dispatch') {
          const matchedVehicle = this.vehicleNumbers.find(v => String(v.id) === String(vehicle_id));
          if (matchedVehicle) {
            this.selectedVehicleNo = matchedVehicle.id;
          } else {
            this.selectedVehicleNo = vehicle_id;
          }
        }

        this.$nextTick(() => {
          if (
            (this.returnType === 'with_dispatch' && this.selectedVehicleNo) ||
            (this.returnType === 'without_dispatch' && this.selectedSalesRep)
          ) {
            this.performSearch();
          }
        });
      }
    },

    //Fetch Vehicle Numbers
    loadVehicleNumbers() {
      const profileId = localStorage.getItem('profile_id');

      if (!this.token) {
        console.error('Token is missing');
        return Promise.resolve();
      }

      return fetchVehicleListByProfileGateIn(profileId, this.token)
        .then(response => {
          if (response.data.status === 'success') {
            this.vehicleNumbers = response.data.data;
          } else {
            console.error('Failed to fetch vehicle numbers');
          }
        })
        .catch(error => {
          console.error('Error fetching vehicle numbers:', error);
        });
    },

    async fetchSalesRepresentatives() {

      this.loading = true;
      try {
        const response = await fetchSalesReps(this.profileId, this.profileId, this.token);

        const reps = response?.data?.data || [];
        this.salesRep = reps.map(r => ({
          profile_id: String(r.profile_id),
          name: r.name
        }));

        this.selectedSalesRep = "";
      } catch (error) {
        console.error("Error fetching sales reps:", error);
      } finally {
        this.loading = false;
      }
    },

    //Fetch dispatch items
    loadDispatchItems() {
      if (!this.selectedVehicleNo) return;

      dispatchlistitem(this.selectedVehicleNo, this.token)
          .then((response) => {
            if (response.data.status === 'success') {
              this.dispatchItems = response.data.data.map((item) => ({
                ...item,
                security_counted_qty: 0,
              }));
            }
          })
          .catch((error) => {
            console.error('Error fetching dispatch items:', error);
          });
    },

    //Issue gate pass
    issueGatePass() {
      const toast = useToast();
      this.validationError = '';

      const filteredDetails = this.dispatchItems.filter(item =>
        (parseInt(item.normal_counted_qty) || 0) > 0 ||
        (parseInt(item.damage_counted_qty) || 0) > 0
      );

      if (filteredDetails.length === 0) {
        toast.error('Please enter at least one counted quantity.');
        return;
      }

      // if (!this.validateCountedQuantities()) {
      //   return;
      // }

      this.loading2 = true;

      const activeProfileId = this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId;

      const payload = {
        ...(this.returnType === 'with_dispatch'
          ? { vehicle_id: this.selectedVehicleNo }
          : { sales_rep_id: this.selectedSalesRep, date: this.startDate }),

        return_type: this.returnType,
        created_by: activeProfileId,
        security_checked_by: activeProfileId,
        created_at: this.startDate,
        security_checked_at: new Date().toISOString().split('T')[0],
        remark: this.gatePassRemark || '',
        details: filteredDetails.map(item => ({
          item_id: item.item_id,
          item_qty: item.qty,
          normal_return_qty: parseInt(item.normal_return_qty),
          damage_return_qty: parseInt(item.damage_return_qty),
          normal_counted_qty: parseInt(item.normal_counted_qty) || 0,
          damage_counted_qty: parseInt(item.damage_counted_qty) || 0,
          remark: item.remark || '',
        })),
      };

      savegetpassin(payload, this.token)
        .then(response => {
          if (response.data.status === 'success') {
            toast.success('Gate pass issued successfully!');
            this.gatePassSaved = true;
          } else {
            toast.error('Failed to issue gate pass.');
          }
        })
        .catch(error => {
          console.error('Error issuing gate pass:', error);
          toast.error('Error issuing gate pass: ' + error.message);
        })
        .finally(() => {
          this.loading2 = false;
        });
    },

    //Reset Form
    resetForm() {
      this.dispatchItems = [];
      this.showTable = false; // Hide the table when form is reset
      this.gatePassSaved = false;
      this.validationError = '';
      this.gatePassRemark = '';
    },

    //Action when click on the reset button
    cancel() {
      this.resetForm();
    },

    //Handle date range
    handleDateChange() {
      console.log('Date changed:', this.startDate);
    },

    //Filter Gate Passes
    performSearch() {
      const toast = useToast();
      this.resetForm();
      const date = this.startDate;

      this.loading = true;
      let payload = {
        return_type: this.returnType,
        date: date,
      };

      if (this.returnType === 'with_dispatch') {

        if (!this.selectedVehicleNo) {
          toast.error('Please select a vehicle number.');
          return;
        }

        payload.vehicel_id = this.selectedVehicleNo;
      }

      if (this.returnType === 'without_dispatch') {

        if (!this.selectedSalesRep) {
          toast.error('Please select a sales rep.');
          return;
        }

        payload.sales_rep_id = this.selectedSalesRep;
      }

      fetchDispatchItemsSearchGatePassIn(payload, this.token)
          .then(response => {
            if (response.data.status === 'success') {
              this.dispatchItems = response.data.data.map((item) => ({
                ...item,
                normal_counted_qty: 0,
                damage_counted_qty: 0,
                remark: '',
                _hasError: false,
              }));
              this.showTable = true; // Show the table when search is successful
            } else {
              this.dispatchItems = []; // Clear dispatch items if no data found
              this.showTable = true; // Show the table to display "No data found" message
            }
          })
          .catch(error => {
            console.error('Error performing search:', error);
            toast.error('Error performing search: ' + error.message);
          })
          .finally(() => {
            this.loading = false; // Stop loading
          });
    },

    computedBalance(item) {
      return (parseInt(item.qty) || 0) - (parseInt(item.invoiced_qty) || 0);
    },

    clearRowError(item) {
      item._hasError = false;
      if (!this.dispatchItems.some(i => i._hasError)) {
        this.validationError = '';
      }
    },

    validateCountedQuantities() {
      const toast = useToast();
      let hasError = false;
      const errors = [];

      this.dispatchItems.forEach(item => {
        item._hasError = false;

        const normalCounted  = parseInt(item.normal_counted_qty)  || 0;
        const damageCounted  = parseInt(item.damage_counted_qty)  || 0;
        const normalReturn   = parseInt(item.normal_return_qty)   || 0;
        const damageReturn   = parseInt(item.damage_return_qty)   || 0;

        if (this.returnType === 'with_dispatch') {
          const balance = this.computedBalance(item);
          const expectedNormal  = balance + normalReturn;
          const expectedDamaged = damageReturn;

          if (normalCounted !== expectedNormal || damageCounted !== expectedDamaged) {
            item._hasError = true;
            hasError = true;
            errors.push(
              `"${item.item_name}": Normal Counted quantity must be ${expectedNormal}, Damaged Counted quantity must be ${expectedDamaged}.`
            );
          }
        } else {
          if (normalCounted !== normalReturn || damageCounted !== damageReturn) {
            item._hasError = true;
            hasError = true;
            errors.push(
              `"${item.item_name}": Normal Counted quantity must equal Return Normal (${normalReturn}), Damaged Counted quantity must equal Return Damaged (${damageReturn}).`
            );
          }
        }
      });

      if (hasError) {
        errors.forEach(msg => toast.error(msg));
      }

      return !hasError;
    },

    printGatePass() {
      const printWindow = window.open('', '_blank');

      const vehicleNo =
        this.vehicleNumbers.find(v => v.id == this.selectedVehicleNo)?.vehicle_no || '';

      const isWithDispatch = this.returnType === 'with_dispatch';

      const filteredItems = this.dispatchItems.filter(item => {
        const qty = parseInt(item.qty) || 0;
        const invoicedQty = parseInt(item.invoiced_qty) || 0;
        const balance = this.computedBalance(item) || 0;
        const normalReturn = parseInt(item.normal_return_qty) || 0;
        const damageReturn = parseInt(item.damage_return_qty) || 0;
        const normalCounted = parseInt(item.normal_counted_qty) || 0;
        const damageCounted = parseInt(item.damage_counted_qty) || 0;

        if (isWithDispatch) {
          return (
            qty > 0 ||
            invoicedQty > 0 ||
            balance > 0 ||
            normalReturn > 0 ||
            damageReturn > 0 ||
            normalCounted > 0 ||
            damageCounted > 0
          );
        } else {
          return (
            normalReturn > 0 ||
            damageReturn > 0 ||
            normalCounted > 0 ||
            damageCounted > 0
          );
        }
      });

      const itemRows = filteredItems.length > 0 ? filteredItems.map(item => `
        <tr>
          <td>${item.item_name}</td>
          ${isWithDispatch ? `
            <td class="text-center">${parseInt(item.qty) || 0}</td>
            <td class="text-center">${parseInt(item.invoiced_qty) || 0}</td>
            <td class="text-center">${this.computedBalance(item)}</td>
          ` : ''}
          <td class="text-center">${parseInt(item.normal_return_qty) || 0}</td>
          <td class="text-center">${parseInt(item.damage_return_qty) || 0}</td>
          <td class="text-center">${parseInt(item.normal_counted_qty) || 0}</td>
          <td class="text-center">${parseInt(item.damage_counted_qty) || 0}</td>
        </tr>
      `).join('') : `
        <tr>
          <td colspan="${isWithDispatch ? 8 : 5}" class="text-center" style="padding: 16px; color: #666;">No items with non-zero quantities found.</td>
        </tr>
      `;

      printWindow.document.write(`
        <html>
          <head>
            <title>Gate Pass In</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                font-size: 13px;
              }

              .container {
                max-width: 900px;
                margin: auto;
              }

              h2 {
                text-align: center;
                margin-bottom: 20px;
              }

              .header-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
                margin-top: 20px;
              }

              .info-box {
                border: 1px solid #ddd;
                padding: 10px;
                border-radius: 5px;
                margin-bottom: 20px;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
              }

              th, td {
                border: 1px solid #000;
                padding: 6px;
                text-align: center;
              }

              th {
                background-color: #f2f2f2;
              }

              .signature-section {
                margin-top: 40px;
                display: flex;
                justify-content: space-between;
              }

              .signature-box {
                width: 40%;
                text-align: center;
              }

              @page {
                size: A4 landscape;
                margin: 10mm;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h2>Gate Pass In</h2>

              <div class="info-box">
                  <p><strong>Distributor Name:</strong> ${this.distributorName}</p>
                  <p><strong>Dispatch Date:</strong> ${this.startDate}</p>
                  <p><strong>Vehicle Number:</strong> ${vehicleNo}</p>
              </div>

              <table>
                <thead>
                  <tr>
                    <th rowspan="2">Item Name</th>
                    ${isWithDispatch ? `
                      <th rowspan="2">Loaded</th>
                      <th rowspan="2">Invoiced</th>
                      <th rowspan="2">Balance</th>
                    ` : ''}
                    <th colspan="2">Return</th>
                    <th colspan="2">Counted Quantity</th>
                  </tr>
                  <tr>
                    <th>Normal</th>
                    <th>Damaged</th>
                    <th>Normal</th>
                    <th>Damaged</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemRows}
                </tbody>
              </table>

              <div style="margin-top: 16px;">
                <strong>Remark:</strong> ${this.gatePassRemark || '—'}
              </div>

              <div class="header-grid">
                <div class="info-box">
                    <p><strong>Generated By:</strong> ${this.distributorName}</p>
                    <p><strong>Generated Date & Time:</strong> ${new Date().toLocaleString()}</p>
                </div>

                <div class="info-box">
                    <p><strong>Verified By:</strong> <p>_________________________</p></p>
                    <p><strong>Verified Date & Time:</strong> <p>_________________________</p></p>
                </div>
              </div>

            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  },
};
</script>

<style scoped>
.quantity-input {
  width: 120px !important;
}

.group-end {
  border-right: 3px solid #fff;
}

/* Single Line Filter Row Layout */
.filter-row-single {
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 16px;
  width: 100%;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.filter-item.button-item {
  flex: 0 0 auto;
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0;
  white-space: nowrap;
}

.filter-control {
  height: 42px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 0.9rem;
  color: #1e293b;
  padding: 0 12px;
  width: 100%;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.filter-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
  outline: none;
}

.btn-search-single {
  height: 42px;
  padding: 0 24px;
  background-color: #007bff;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 6px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-search-single:hover {
  background-color: #0056b3;
  box-shadow: 0 4px 6px -1px rgba(0, 123, 255, 0.2);
}

.btn-search-single:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Modern Empty State */
.modern-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  text-align: center;
}

.empty-state-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #eff6ff;
  color: #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.empty-state-icon {
  font-size: 1.6rem;
}

.empty-state-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.empty-state-description {
  font-size: 0.85rem;
  color: #64748b;
  max-width: 420px;
  margin: 0;
  line-height: 1.5;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 16px;
  text-align: center;
  color: #007bff;
  font-weight: 500;
}

.error-message {
  color: #dc2626;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
}

.table-scroll-container {
  max-height: 55vh;
  overflow-y: auto;
}

/* Modern Table Container Card */
.modern-table-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.modern-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

/* Header Styling */
.modern-table thead {
  background-color: #1e293b;
}

.modern-table .header-cell {
  background-color: #1e293b !important;
  color: #f8fafc !important;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 12px 14px;
  border-bottom: 1px solid #334155 !important;
  border-top: none !important;
  white-space: nowrap;
}

.modern-table .group-header {
  border-bottom: 2px solid #3b82f6 !important;
  background-color: #0f172a !important;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.modern-table .sub-header {
  font-size: 0.78rem;
  font-weight: 600;
  color: #cbd5e1 !important;
  padding: 10px 14px;
  background-color: #1e293b !important;
}

.modern-table .group-end {
  border-right: 2px solid #334155 !important;
}

/* Sticky Header */
.table-scroll-container thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Row & Cell Styling */
.modern-row {
  transition: background-color 0.15s ease;
}

.modern-row td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 0.88rem;
  color: #334155;
}

.modern-row:hover {
  background-color: #f8fafc;
}

.modern-row:last-child td {
  border-bottom: none;
}

.modern-row.row-error {
  background-color: #fef2f2 !important;
}

/* Component Badges */
.item-name-cell {
  font-weight: 600;
  color: #0f172a;
}

.item-icon {
  color: #3b82f6;
  font-size: 0.9rem;
}

.qty-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 600;
  font-size: 0.85rem;
}

.qty-badge.invoiced {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.balance-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  background-color: #f1f5f9;
  color: #64748b;
  font-weight: 600;
  font-size: 0.83rem;
  min-width: 32px;
}

.balance-badge.has-balance {
  background-color: #fef3c7;
  color: #b45309;
}

.return-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.83rem;
}

.return-badge.normal {
  background-color: #e0f2fe;
  color: #0369a1;
}

.return-badge.normal.is-zero {
  background-color: #f1f5f9;
  color: #94a3b8;
}

.return-badge.damage {
  background-color: #f1f5f9;
  color: #94a3b8;
}

.return-badge.damage.has-damage {
  background-color: #fee2e2;
  color: #b91c1c;
}

.col-item-name {
  min-width: 220px;
}

.col-counted {
  width: 90px;
  max-width: 100px;
}

/* Modern Inputs */
.quantity-input.modern-input {
  width: 70px !important;
  height: 34px;
  padding: 4px 6px;
  text-align: center;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.quantity-input.modern-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  outline: none;
  background-color: #fff;
}

.quantity-input.modern-input:disabled {
  background-color: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.quantity-input.modern-input.is-invalid {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
}

/* Remark & Actions Section */
.modern-remark-container label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;
}

.modern-textarea {
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 10px 14px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.modern-textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  outline: none;
}

@media (max-width: 768px) {
  .filter-row-single {
    flex-wrap: wrap;
  }
  .filter-item {
    flex: 1 1 100%;
  }
  .filter-item.button-item {
    flex: 1 1 100%;
  }
  .btn-search-single {
    width: 100%;
  }
}

@media (max-height: 600px) {
  .table-scroll-container {
    max-height: 75vh;
  }
}

@media (min-height: 900px) {
  .table-scroll-container {
    max-height: 50vh;
  }
}

@media (min-height: 1200px) {
  .table-scroll-container {
    max-height: 60vh;
  }
}
</style>